/* @jsxImportSource @emotion/react */
import React from 'react'

import { ChevronLeftIcon } from '@slite/icons'

import { ArticleAuthors } from './ArticleAuthors'
import { ArticleCtaButton } from './ArticleCtaButton'
import { ARTICLE_SIDEBAR_BREAKPOINT, fontSize } from './constants'
import type { ArticleAuthor } from './types'

const META_SEPARATOR = '·'

const titleCss = {
  fontSize: fontSize.title,
  lineHeight: 1.3,
  letterSpacing: '-0.02em',
  textWrap: 'balance',
  [ARTICLE_SIDEBAR_BREAKPOINT]: { fontSize: 'var(--font-size-2, 2.25rem)' },
} as const

// Copy on the left, the image on the right, its top level with the meta line.
const withAsideCss = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) 480px',
  columnGap: 96,
  alignItems: 'start',
  [ARTICLE_SIDEBAR_BREAKPOINT]: { gridTemplateColumns: '1fr', rowGap: 32 },
} as const

const descriptionCss = {
  fontSize: fontSize.lead,
  lineHeight: 1.5,
  textWrap: 'balance',
} as const

const dividerCss = {
  width: 1,
  margin: '12px 8px',
  alignSelf: 'stretch',
  background: 'var(--shade-40)',
  [ARTICLE_SIDEBAR_BREAKPOINT]: { display: 'none' },
} as const

export function ArticleHeader(props: {
  title: string
  description?: string
  date: string
  dateTime?: string
  readingTime?: string
  category?: string
  authors: ArticleAuthor[]
  authorsLabel?: string
  backLabel: string
  backHref: string
  ctaLabel?: string
  ctaHref?: string
  /** Rendered beside the title, e.g. the hero image when there is no video. */
  aside?: React.ReactNode
}) {
  const metaParts = [props.readingTime, props.category].filter(Boolean)

  return (
    <header>
      <a
        href={props.backHref}
        className="flexbox-inline flexbox-align-center flexbox-gap-6 shade-20 shade-05-on-hover"
        css={{ fontSize: fontSize.body }}
      >
        <ChevronLeftIcon size={14} aria-hidden="true" />
        {props.backLabel}
      </a>

      <div css={[{ marginTop: 48 }, props.aside ? withAsideCss : undefined]}>
        <div>
          <div
            className="shade-00 font-weight-semibold margin-bottom-16"
            css={{ fontSize: fontSize.small }}
          >
            <time dateTime={props.dateTime}>{props.date}</time>
            {metaParts.map((part) => (
              <React.Fragment key={part}>
                <span className="padding-horizontal-8" aria-hidden="true">
                  {META_SEPARATOR}
                </span>
                {part}
              </React.Fragment>
            ))}
          </div>

          <h1 className="font-family-title font-weight-medium" css={titleCss}>
            {props.title}
          </h1>
          {props.description && (
            <p
              className="shade-10 margin-top-16 margin-bottom-40"
              css={descriptionCss}
            >
              {props.description}
            </p>
          )}

          <div className="flexbox flexbox-align-center flexbox-gap-20 flexbox-wrap margin-top-24">
            <ArticleAuthors
              authors={props.authors}
              label={props.authorsLabel}
            />
            {props.ctaLabel && props.ctaHref && (
              <React.Fragment>
                {props.authors.length > 0 && <div css={dividerCss} />}
                <ArticleCtaButton
                  label={props.ctaLabel}
                  href={props.ctaHref}
                  data-test-id="articleLayoutHeaderCta"
                />
              </React.Fragment>
            )}
          </div>
        </div>
        {props.aside}
      </div>
    </header>
  )
}
